import React, { useState, useEffect, useCallback, useMemo, Fragment } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Cancel as CancelIcon,
  ExpandMore as ExpandMoreIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { lt } from 'date-fns/locale';
import {
  updateAvailableSlots,
  deleteReservation,
  updateReservation,
  generateTimeSlots,
  coachToggleSlot,
  getSlotVenueBackground,
  getReservationCoveringSlot,
  normalizeSlot,
  ALL_TRAINING_VENUE_IDS,
  TRAINING_VENUES,
  subscribeToAvailableSlotsForRange,
  subscribeToReservationsForRange,
  subscribeToAllReservations,
  getCoachByUid,
  DEFAULT_COACH_ID,
  formatReservationCourtLabel
} from '../utils/firebaseUtils';
import ScrollableBookingCalendar from './ScrollableBookingCalendar';
import {
  getBookingCalendarDates,
  getBookingCalendarRange,
  getUnavailableSlotColor
} from '../utils/calendarDateUtils';
import { UNAVAILABLE_COLOR } from '../utils/venueCalendarUtils';
import { isPastDateTime } from '../utils/dateUtils';
import { isUserAdmin } from '../utils/adminUtils';
import { useAuth } from '../contexts/AuthContext';
import AdminDashboard from './AdminDashboard';

const AdminPage = () => {
  const { currentUser, approvedCoachUids } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const currentCoach = currentUser ? getCoachByUid(currentUser.uid) : null;
  const hasCoachPanel = currentCoach != null || (approvedCoachUids && approvedCoachUids.includes(currentUser?.uid));
  const currentCoachId = currentCoach?.id ?? (approvedCoachUids?.includes(currentUser?.uid) ? currentUser.uid : DEFAULT_COACH_ID);
  const isAdmin = isUserAdmin(currentUser);
  const isAdminOnly = isAdmin && !hasCoachPanel;
  const isVisosRezervacijos = location.pathname.includes('/visos-rezervacijos');
  const isAdministracija = location.pathname.includes('/administracija');
  const reservationsTab = isAdministracija ? 2 : isVisosRezervacijos ? 1 : 0;

  // Admin-only: redirect /admin to dashboard; allow /admin/visos-rezervacijos
  React.useEffect(() => {
    if (isAdminOnly && location.pathname === '/admin') {
      navigate('/admin/administracija', { replace: true });
    }
  }, [isAdminOnly, location.pathname, navigate]);

  const [availableSlots, setAvailableSlots] = useState({});
  const [weekReservations, setWeekReservations] = useState([]);
  const [loading, setLoading] = useState(true);

  const [allReservations, setAllReservations] = useState([]);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [reservationDialog, setReservationDialog] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [reservationToCancel, setReservationToCancel] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [reservationToDelete, setReservationToDelete] = useState(null);
  const [showPastReservations, setShowPastReservations] = useState(false);

  const [error, setError] = useState('');

  const calendarDates = useMemo(() => getBookingCalendarDates(), []);
  const bookingRange = useMemo(() => getBookingCalendarRange(), []);

  const loadWeekData = useCallback(() => {
    setLoading(true);
    try {
      const { startDate, endDate } = bookingRange;
      const cId = currentCoachId || DEFAULT_COACH_ID;

      const unsubscribeSlots = subscribeToAvailableSlotsForRange(startDate, endDate, (slots) => {
        setAvailableSlots(slots);
        setLoading(false);
      }, cId);

      const unsubscribeReservations = subscribeToReservationsForRange(startDate, endDate, (reservations) => {
        setWeekReservations(reservations);
      }, cId);

      return () => {
        unsubscribeSlots();
        unsubscribeReservations();
      };
    } catch (error) {
      console.error('Error setting up calendar data listeners:', error);
      setError('Klaida kraunant duomenis');
      setLoading(false);
    }
  }, [bookingRange, currentCoachId]);

  const loadAllReservations = useCallback(() => {
    try {
      const cId = isUserAdmin(currentUser) ? null : (currentCoachId || DEFAULT_COACH_ID);
      const unsubscribe = subscribeToAllReservations((reservations) => {
        setAllReservations(reservations);
      }, cId);
      
      return unsubscribe;
    } catch (error) {
      console.error('Error setting up all reservations listener:', error);
    }
  }, [currentUser, currentCoachId]);

  useEffect(() => {
    if (!hasCoachPanel && !(isAdminOnly && isVisosRezervacijos)) {
      setLoading(false);
      return;
    }
    if (isAdminOnly && isVisosRezervacijos) {
      const unsubscribe = loadAllReservations();
      setLoading(false);
      return () => { if (unsubscribe) unsubscribe(); };
    }
    setLoading(true);
    const cleanupWeekData = loadWeekData();
    const cleanupAllReservations = loadAllReservations();
    return () => {
      if (cleanupWeekData) cleanupWeekData();
      if (cleanupAllReservations) cleanupAllReservations();
    };
  }, [hasCoachPanel, isAdminOnly, isVisosRezervacijos, loadWeekData, loadAllReservations]);

  const handleTimeSlotClick = async (date, time) => {
    if (isPastDateTime(date, time)) {
      setError('Negalima keisti praeities laikų');
      return;
    }
    try {
      let currentSlots = availableSlots[date]?.timeSlots;

      if (!currentSlots) {
        currentSlots = timeSlots.map((timeSlot) => ({
          time: timeSlot,
          duration: 30,
          venues: { seb_arena: false, vrm_courts: false, bernardinai_courts: false }
        }));
      }

      const currentSlot = currentSlots.find((s) => s.time === time);
      const toggled = coachToggleSlot(currentSlot || { time }, ALL_TRAINING_VENUE_IDS);

      const updatedTimeSlots = currentSlots.map((slot) =>
        slot.time === time ? toggled : normalizeSlot(slot)
      );

      setAvailableSlots({
        ...availableSlots,
        [date]: {
          date,
          timeSlots: updatedTimeSlots
        }
      });

      await updateAvailableSlots(date, updatedTimeSlots, currentCoachId);

      setError('');
    } catch (error) {
      console.error('Error saving time slot:', error);
      setError('Klaida išsaugant laiko slotą');
    }
  };





  const handleOpenDeleteConfirm = (reservation, e) => {
    e?.stopPropagation?.();
    setReservationToDelete(reservation);
    setDeleteConfirmOpen(true);
  };

  const handleCloseDeleteConfirm = () => {
    setDeleteConfirmOpen(false);
    setReservationToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!reservationToDelete) return;
    try {
      await deleteReservation(reservationToDelete.id);
      handleCloseDeleteConfirm();
      // Real-time listeners will automatically update the UI
    } catch (error) {
      console.error('Error deleting reservation:', error);
      setError('Klaida ištrinant rezervaciją');
    }
  };

  const handleUpdateReservationStatus = async (reservationId, status) => {
    try {
      await updateReservation(reservationId, { status });
      setCancelConfirmOpen(false);
      setReservationToCancel(null);
      // Real-time listeners will automatically update the UI
    } catch (error) {
      console.error('Error updating reservation:', error);
      setError('Klaida atnaujinant rezervaciją');
    }
  };

  const handleOpenCancelConfirm = (reservation, e) => {
    e?.stopPropagation?.();
    setReservationToCancel(reservation);
    setCancelConfirmOpen(true);
  };

  const handleCloseCancelConfirm = () => {
    setCancelConfirmOpen(false);
    setReservationToCancel(null);
  };

  const handleReservationClick = (reservation) => {
    setSelectedReservation(reservation);
    setReservationDialog(true);
  };

  const handleCloseReservationDialog = () => {
    setReservationDialog(false);
    setSelectedReservation(null);
  };

  const getVenueColor = (locationId) =>
    TRAINING_VENUES.find((v) => v.id === locationId)?.color || '#e0e0e0';

  const getSlotCellStyle = (date, time) => {
    const reservation = getReservationCoveringSlot(date, time, weekReservations);
    if (reservation) {
      const venueColor = getVenueColor(reservation.location);
      return {
        background: venueColor,
        backgroundColor: venueColor,
        isBooked: true,
        reservation
      };
    }
    const slot = availableSlots[date]?.timeSlots?.find((s) => s.time === time);
    const bg = getSlotVenueBackground(slot, ALL_TRAINING_VENUE_IDS);
    const isGradient = typeof bg === 'string' && bg.includes('gradient');
    const isUnavailable = !isGradient && bg === UNAVAILABLE_COLOR;
    const fillColor = isUnavailable ? getUnavailableSlotColor(date) : bg;
    return {
      background: isUnavailable ? fillColor : bg,
      backgroundColor: isGradient ? undefined : fillColor,
      isBooked: false,
      reservation: null
    };
  };

  const timeSlots = generateTimeSlots();

  // Sort and separate reservations by date (exclude preview coach seeded data from admin view)
  const getSortedAndSeparatedReservations = () => {
    const filtered = allReservations.filter((r) => r.coachId !== 'preview');
    if (!filtered.length) return { upcoming: [], past: [] };

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const sortedReservations = [...filtered].sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      
      if (dateA.getTime() !== dateB.getTime()) {
        return dateA.getTime() - dateB.getTime();
      }
      
      // If same date, sort by time
      return a.startTime.localeCompare(b.startTime);
    });

    const upcoming = [];
    const past = [];

    sortedReservations.forEach(reservation => {
      const reservationDate = new Date(reservation.date);
      reservationDate.setHours(0, 0, 0, 0);

      if (reservationDate >= today) {
        upcoming.push(reservation);
      } else {
        past.push(reservation);
      }
    });

    // Sort upcoming events: nearest first (ascending order) - same as AdminReservationsLogPage
    upcoming.sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      
      if (dateA.getTime() !== dateB.getTime()) {
        return dateA.getTime() - dateB.getTime();
      }
      
      return a.startTime.localeCompare(b.startTime);
    });

    // Sort past events: most recent first (descending order)
    past.sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      
      if (dateA.getTime() !== dateB.getTime()) {
        return dateB.getTime() - dateA.getTime(); // Reverse order for descending
      }
      
      // If same date, sort by time (earlier time first)
      return a.startTime.localeCompare(b.startTime);
    });

    return { upcoming, past };
  };

  if (isAdminOnly && !isVisosRezervacijos) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Typography variant="h3" component="h1" sx={{ mb: 4, textAlign: 'center', fontWeight: 600 }}>
          Administracija
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
        <AdminDashboard />
      </Container>
    );
  }

  if (loading && !(isAdminOnly && isVisosRezervacijos)) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const pageTitle = isVisosRezervacijos ? 'Visos rezervacijos' : 'Kalendorius';

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Typography variant="h3" component="h1" sx={{ mb: 4, textAlign: 'center', fontWeight: 600 }}>
        {pageTitle}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {reservationsTab === 0 && (
        <Box
          sx={{
            flex: 1,
            minHeight: { xs: '70dvh', md: '75dvh' },
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <Box
            sx={{
              flex: 1,
              minHeight: { xs: 280, md: 320 },
              display: 'flex',
              flexDirection: 'column',
              mb: { xs: 2, md: 3 }
            }}
          >
            <ScrollableBookingCalendar
              calendarDates={calendarDates}
              timeSlots={timeSlots}
              fillContainer
              renderSlotCell={(date, time, index) => {
                const cellStyle = getSlotCellStyle(date, time);
                const reservation = cellStyle.reservation;
                const isHourEnd = (index + 1) % 2 === 0;
                const isPast = isPastDateTime(date, time);
                return (
                  <Box
                    key={`${date}-${time}`}
                    onClick={() => { if (!isPast) handleTimeSlotClick(date, time); }}
                    sx={{
                      height: { xs: 32, sm: 28, md: 30 },
                      background: cellStyle.background,
                      backgroundColor: cellStyle.backgroundColor || getUnavailableSlotColor(date),
                      border: cellStyle.isBooked ? '4px solid' : '1px solid',
                      borderColor: cellStyle.isBooked ? '#d32f2f' : 'grey.300',
                      cursor: isPast ? 'not-allowed' : 'pointer',
                      opacity: isPast ? 0.5 : 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: isHourEnd && index < timeSlots.length - 1 ? 0.25 : 0,
                      position: 'relative',
                      boxSizing: 'border-box'
                    }}
                  >
                    {cellStyle.isBooked && reservation && (
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: { xs: '0.5rem', sm: '0.7rem' },
                          color: 'rgba(0,0,0,0.82)',
                          fontWeight: 600,
                          textAlign: 'center',
                          lineHeight: 1,
                          maxWidth: '100%',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                        title={`${reservation.clientName} - ${reservation.clientPhone}${reservation.courtNo || reservation.sebCourtName ? ` | Aikštelė: ${reservation.courtNo || reservation.sebCourtName}` : ''}`}
                      >
                        {reservation.clientName}
                      </Typography>
                    )}
                  </Box>
                );
              }}
            />
          </Box>
        </Box>
      )}

      {(reservationsTab === 1 || (isAdminOnly && isVisosRezervacijos)) && (
        <>
          {/* Visos Rezervacijos tab (coaches) or page (admin-only) - all reservations by lesson date, past button */}
          <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflow: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>Data</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>Laikas</TableCell>
                {isAdminOnly && isVisosRezervacijos && (
                  <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>Treneris</TableCell>
                )}
                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>Klientas</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>Aikštelė</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>Būsena</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100', width: 120 }}>Veiksmai</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100', width: 48 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {(() => {
                const { upcoming, past } = getSortedAndSeparatedReservations();
                const hasUpcoming = upcoming.length > 0;
                const hasPast = past.length > 0;

                const hasCourtLabel = formatReservationCourtLabel;

                const colSpan = isAdminOnly && isVisosRezervacijos ? 8 : 7;

                const ReservationRow = ({ r }) => (
                  <TableRow key={r.id} hover>
                    <TableCell>{r.date || '—'}</TableCell>
                    <TableCell>{r.startTime || '—'}</TableCell>
                    {isAdminOnly && isVisosRezervacijos && (
                      <TableCell>{r.coachName || '—'}</TableCell>
                    )}
                    <TableCell>{r.clientName || '—'}</TableCell>
                    <TableCell>{hasCourtLabel(r)}</TableCell>
                    <TableCell>
                      {r.status === 'cancelled' ? (
                        <Chip label="Atšaukta" size="small" color="error" variant="outlined" />
                      ) : (
                        <Chip label="Patvirtinta" size="small" color="success" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Atšaukti rezervaciją" arrow>
                          <span>
                            <IconButton size="small" color="warning" onClick={(e) => { e.stopPropagation(); handleOpenCancelConfirm(r, e); }} disabled={r.status === 'cancelled'} sx={r.status === 'cancelled' ? { color: 'grey.400' } : undefined}>
                              <CancelIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={r.status === 'cancelled' ? 'Ištrinti atšauktą rezervaciją' : 'Įrašą galima ištrinti tik atšaukus'} arrow>
                          <span>
                            <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); handleOpenDeleteConfirm(r, e); }} disabled={r.status !== 'cancelled'} sx={r.status !== 'cancelled' ? { color: 'grey.400' } : undefined}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" color="primary" onClick={() => handleReservationClick(r)} title="Plačiau">
                        <ExpandMoreIcon sx={{ transform: 'rotate(-90deg)' }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );

                if (!hasUpcoming && !hasPast) {
                  return (
                    <TableRow>
                      <TableCell colSpan={colSpan} align="center" sx={{ py: 4, color: 'text.secondary' }}>Rezervacijų nėra.</TableCell>
                    </TableRow>
                  );
                }
                if (!hasUpcoming) {
                  return (
                    <TableRow>
                      <TableCell colSpan={colSpan} align="center" sx={{ py: 4, color: 'text.secondary' }}>Būsimų rezervacijų nėra.</TableCell>
                    </TableRow>
                  );
                }

                return upcoming.map((r) => <ReservationRow key={r.id} r={r} />);
              })()}
            </TableBody>
          </Table>
        </TableContainer>

        {getSortedAndSeparatedReservations().past.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                variant="outlined"
                onClick={() => setShowPastReservations(!showPastReservations)}
                endIcon={<KeyboardArrowDownIcon sx={{ transform: showPastReservations ? 'rotate(180deg)' : 'none' }} />}
              >
                {showPastReservations ? 'Slėpti praėjusias rezervacijas' : `Praėjusios rezervacijos (${getSortedAndSeparatedReservations().past.length})`}
              </Button>
            </Box>
            {showPastReservations && (
              <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflow: 'auto', mt: 2 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>Data</TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>Laikas</TableCell>
                      {isAdminOnly && isVisosRezervacijos && (
                        <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>Treneris</TableCell>
                      )}
                      <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>Klientas</TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>Aikštelė</TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>Būsena</TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100', width: 120 }}>Veiksmai</TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100', width: 48 }} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {getSortedAndSeparatedReservations().past.map((r) => {
                      const hasCourtLabel = formatReservationCourtLabel;
                      return (
                        <TableRow key={r.id} hover>
                          <TableCell>{r.date || '—'}</TableCell>
                          <TableCell>{r.startTime || '—'}</TableCell>
                          {isAdminOnly && isVisosRezervacijos && (
                            <TableCell>{r.coachName || '—'}</TableCell>
                          )}
                          <TableCell>{r.clientName || '—'}</TableCell>
                          <TableCell>{hasCourtLabel(r)}</TableCell>
                          <TableCell>
                            {r.status === 'cancelled' ? (
                              <Chip label="Atšaukta" size="small" color="error" variant="outlined" />
                            ) : (
                              <Chip label="Patvirtinta" size="small" color="success" variant="outlined" />
                            )}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Tooltip title="Atšaukti rezervaciją" arrow>
                                <span>
                                  <IconButton size="small" color="warning" onClick={(e) => { e.stopPropagation(); handleOpenCancelConfirm(r, e); }} disabled={r.status === 'cancelled'} sx={r.status === 'cancelled' ? { color: 'grey.400' } : undefined}>
                                    <CancelIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title={r.status === 'cancelled' ? 'Ištrinti atšauktą rezervaciją' : 'Įrašą galima ištrinti tik atšaukus'} arrow>
                                <span>
                                  <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); handleOpenDeleteConfirm(r, e); }} disabled={r.status !== 'cancelled'} sx={r.status !== 'cancelled' ? { color: 'grey.400' } : undefined}>
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <IconButton size="small" color="primary" onClick={() => handleReservationClick(r)} title="Plačiau">
                              <ExpandMoreIcon sx={{ transform: 'rotate(-90deg)' }} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
        </>
      )}

      {reservationsTab === 2 && isUserAdmin(currentUser) && (
        <AdminDashboard />
      )}

      {/* Reservation Details Dialog - same layout as AdminReservationsLogPage */}
      <Dialog open={reservationDialog} onClose={handleCloseReservationDialog} maxWidth="sm" fullWidth>
        {selectedReservation && (
          <>
            <DialogTitle>
              Rezervacija – {selectedReservation.date} {selectedReservation.startTime}
            </DialogTitle>
            <DialogContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 0 }}>
                <Typography variant="body2"><strong>Pamokos data:</strong> {selectedReservation.date} {selectedReservation.startTime}–{selectedReservation.endTime}</Typography>
                <Typography variant="body2"><strong>Klientas:</strong> {selectedReservation.clientName || '—'}</Typography>
                <Typography variant="body2"><strong>Treneris:</strong> {selectedReservation.coachName || '—'}</Typography>
                <Typography variant="body2"><strong>Telefonas:</strong> {selectedReservation.clientPhone || '—'}</Typography>
                <Typography variant="body2"><strong>El. paštas:</strong> {selectedReservation.clientEmail || '—'}</Typography>
                <Typography variant="body2"><strong>Aikštelė:</strong> {formatReservationCourtLabel(selectedReservation)}</Typography>
                <Typography variant="body2"><strong>Aikštelės nr.:</strong> {selectedReservation.courtNo || selectedReservation.sebCourtName || '—'}</Typography>
                <Typography variant="body2"><strong>Būsena:</strong> {selectedReservation.status === 'cancelled' ? 'Atšaukta' : 'Patvirtinta'}</Typography>
                <Typography variant="body2"><strong>Sukūrimo data:</strong> {selectedReservation.createdAt ? format(selectedReservation.createdAt.toDate?.() ?? selectedReservation.createdAt, 'yyyy-MM-dd HH:mm', { locale: lt }) : '—'}</Typography>
                <Typography variant="body2"><strong>Komentarai:</strong> {selectedReservation.comments || '—'}</Typography>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseReservationDialog}>Uždaryti</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Cancel confirmation dialog */}
      <Dialog open={cancelConfirmOpen} onClose={handleCloseCancelConfirm} maxWidth="xs" fullWidth>
        <DialogTitle>Atšaukti rezervaciją?</DialogTitle>
        <DialogContent>
          <Typography>
            Ar tikrai norite atšaukti rezervaciją? Klientui bus išsiųstas el. laiškas apie atšaukimą.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseCancelConfirm} variant="outlined">
            Grįžti
          </Button>
          <Button
            onClick={() => reservationToCancel && handleUpdateReservationStatus(reservationToCancel.id, 'cancelled')}
            variant="contained"
            color="error"
          >
            Atšaukti
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={deleteConfirmOpen} onClose={handleCloseDeleteConfirm} maxWidth="xs" fullWidth>
        <DialogTitle>Ištrinti rezervaciją?</DialogTitle>
        <DialogContent>
          <Typography>
            Ar tikrai norite visam laikui ištrinti šią atšauktą rezervaciją? Šio veiksmo nebus galima atšaukti.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseDeleteConfirm} variant="outlined">
            Grįžti
          </Button>
          <Button onClick={handleConfirmDelete} variant="contained" color="error">
            Ištrinti
          </Button>
        </DialogActions>
      </Dialog>

    </Container>
  );
};

export default AdminPage; 